import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Wrench, Clock, Package, MessageSquare, Loader2, CheckCircle, ArrowLeft, ArrowRight, AlertTriangle, Cpu } from "lucide-react";
import { RepairHeader } from "@/components/RepairHeader";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useWorkflowDefaults } from "@/hooks/useWorkflowDefaults";
import { usePhaseDefaults } from "@/hooks/usePhaseDefaults";
import { useWorkOrderProgress } from "@/hooks/useWorkOrderProgress";
import { usePhaseTimer } from "@/hooks/usePhaseTimer";
import { MatchingManualsPanel } from "@/components/MatchingManualsPanel";
import { ExplodedViewsPanel } from "@/components/workflow/ExplodedViewsPanel";
import { seriesLabelFor } from "@/hooks/useMatchingManuals";
import { AIAssistantDialog } from "@/components/AIAssistantDialog";
import { useAppliance } from "@/hooks/useAppliance";
import { WorkflowFinishActions } from "@/components/WorkflowFinishActions";
import { useTranslation } from "react-i18next";

const PHASE_NAME_KEYS: Record<string, string> = {
  "Safety & Preparation": "safety",
  "Troubleshooting": "troubleshooting",
  "Parts Replacement": "partsReplacement",
  "Final Verification": "verification",
  "Repair": "repair",
  "Verification": "verification",
};

interface RepairStep {
  id: number;
  phase: number;
  phaseName: string;
  title: string;
  description: string;
  isSafetyCritical?: boolean;
  estimatedTime: string;
  completed: boolean;
}

const RepairProcess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("repair");
  const { t: tApp } = useTranslation("appliance");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [manualId, setManualId] = useState<string | null>(null);
  const [manualTitle, setManualTitle] = useState<string | null>(null);
  const [manualIsFallback, setManualIsFallback] = useState(false);

  const { appliance: loadedAppliance, isLoading: isLoadingAppliance, notFound } = useAppliance(id);

  useEffect(() => {
    if (notFound) {
      toast.error(tApp("detail.notFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, tApp]);

  const appliance = loadedAppliance ?? {
    name: tApp("detail.loading"),
    modelNumber: "",
    category: "Appliance",
    brand: "",
  };

  const phaseLabel = (name: string) => {
    const key = PHASE_NAME_KEYS[name];
    return key ? t(`process.phaseNames.${key}`) : name;
  };

  const { defaults, isLoading } = useWorkflowDefaults(
    appliance.brand,
    appliance.category,
    "repair",
    appliance.modelNumber
  );

  const { isPhaseEnabled } = usePhaseDefaults(appliance.category, appliance.brand);

  const { workOrderId, savedProgress, saveProgress } = useWorkOrderProgress(id, "repair");

  // Build repair steps from workflow data
  const steps: RepairStep[] = useMemo(() => {
    const allSteps: RepairStep[] = [];
    let stepId = 1;
    let phaseNum = 1;

    const addPhase = (
      items: { title: string; description: string | null; estimated_minutes?: number | null }[],
      phaseName: string,
      phaseCategory: string,
      isSafety = false
    ) => {
      if (!isPhaseEnabled(phaseCategory, "repair")) return;
      if (items.length === 0) return;
      const currentPhase = phaseNum;
      items.forEach((item) => {
        const m = item.estimated_minutes;
        let estimatedTime = "";
        if (m != null && Number.isFinite(Number(m))) {
          const num = Number(m);
          estimatedTime = num <= 0 ? "" : `${num} min`;
        }
        // else: missing in workflow management → counts as 0 (no time displayed)
        allSteps.push({
          id: stepId++,
          phase: currentPhase,
          phaseName,
          title: item.title,
          description: item.description || "",
          isSafetyCritical: isSafety,
          estimatedTime,
          completed: false,
        });
      });
      phaseNum++;
    };

    addPhase(defaults.refurbishStart, "Safety & Preparation", "refurbish_start", true);
    addPhase(defaults.troubleshooting, "Troubleshooting", "troubleshooting");
    addPhase(defaults.partsReplacement, "Parts Replacement", "parts_replacement");
    addPhase(defaults.verification, "Final Verification", "verification");

    if (allSteps.length === 0) {
      return [
        { id: 1, phase: 1, phaseName: "Safety & Preparation", title: "Safety preparation and power disconnection", description: "Disconnect all power sources before proceeding.", isSafetyCritical: true, estimatedTime: "10 min", completed: false },
        { id: 2, phase: 1, phaseName: "Safety & Preparation", title: "Access component area", description: "Open panels to access the faulty components.", isSafetyCritical: false, estimatedTime: "10 min", completed: false },
        { id: 3, phase: 2, phaseName: "Repair", title: "Remove faulty parts", description: "Carefully remove the identified faulty parts.", isSafetyCritical: false, estimatedTime: "15 min", completed: false },
        { id: 4, phase: 2, phaseName: "Repair", title: "Install replacement parts", description: "Install new parts and secure connections.", isSafetyCritical: false, estimatedTime: "15 min", completed: false },
        { id: 5, phase: 3, phaseName: "Verification", title: "Test functionality", description: "Run a test cycle to verify proper operation.", isSafetyCritical: false, estimatedTime: "15 min", completed: false },
        { id: 6, phase: 3, phaseName: "Verification", title: "Final quality check", description: "Complete final inspection and documentation.", isSafetyCritical: false, estimatedTime: "10 min", completed: false },
      ];
    }
    return allSteps;
  }, [defaults, isPhaseEnabled]);

  // Restore saved progress
  useEffect(() => {
    if (savedProgress && steps.length > 0 && !progressLoaded) {
      setCurrentStepIndex(Math.min(savedProgress.currentStepIndex, steps.length - 1));
      setCompletedSteps(savedProgress.completedSteps || []);
      setProgressLoaded(true);
    }
  }, [savedProgress, steps.length, progressLoaded]);

  // Save progress whenever step/completion changes
  useEffect(() => {
    if (steps.length === 0) return;
    if (savedProgress && !progressLoaded) return;
    if (!progressLoaded) {
      setProgressLoaded(true);
    }

    const cs = steps[currentStepIndex];
    saveProgress({
      currentStepIndex,
      completedSteps,
      currentPhaseName: cs?.phaseName || "",
      currentPhaseCategory: "repair",
      currentStepTitle: cs?.title || "",
      totalSteps: steps.length,
      completedCount: completedSteps.length,
    });
  }, [currentStepIndex, completedSteps, steps.length, progressLoaded, saveProgress]);

  const currentStep = steps[currentStepIndex];
  const isStepCompleted = completedSteps.includes(currentStepIndex);
  const progressPercent = steps.length > 0 ? Math.round((completedSteps.length / steps.length) * 100) : 0;

  // Anonymous phase time tracking — derived from current step's phase name
  const phaseNameToCategory: Record<string, { category: string; label: string }> = {
    "Safety & Preparation": { category: "refurbish_start", label: "Safety & Preparation" },
    "Troubleshooting": { category: "troubleshooting", label: "Pre-repair Troubleshooting" },
    "Parts Replacement": { category: "parts_replacement", label: "Parts Replacement Guides" },
    "Final Verification": { category: "verification", label: "Final Verification" },
    "Repair": { category: "parts_replacement", label: "Parts Replacement Guides" },
    "Verification": { category: "verification", label: "Final Verification" },
  };
  const currentPhaseInfo = currentStep ? phaseNameToCategory[currentStep.phaseName] : undefined;
  usePhaseTimer({
    phaseCategory: currentPhaseInfo?.category,
    phaseLabel: currentPhaseInfo?.label,
    applianceId: id,
    applianceType: appliance.category,
    brand: appliance.brand,
  });

  const handleCompleteStep = useCallback(() => {
    if (!isStepCompleted) {
      setCompletedSteps((prev) => [...prev, currentStepIndex]);
    }
  }, [currentStepIndex, isStepCompleted]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [currentStepIndex, steps.length]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  return (
    <div className="min-h-screen bg-background">
      <RepairHeader
        productName={appliance.name}
        modelNumber={appliance.modelNumber}
        category={appliance.category}
      />

      <main className="p-6 max-w-5xl mx-auto">
        {/* Repair Process Header Card */}
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-repair/10 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-repair" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{t("process.title")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("process.stepOf", { current: currentStepIndex + 1, total: steps.length })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{t("process.overall")}</span>
              <span className="text-lg font-bold text-foreground">{progressPercent}%</span>
            </div>
          </div>
          <Progress value={progressPercent} className="h-2 mt-4" />
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">{t("process.loading")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Step Card */}
            <div className="lg:col-span-2 space-y-4">
              <MatchingManualsPanel
                brand={appliance.brand}
                modelNumber={appliance.modelNumber}
                activeManualId={manualId}
                onSelectForAI={(mid, title, kind) => {
                  setManualId(mid);
                  setManualTitle(title);
                  setManualIsFallback(kind === "fallback");
                  toast.success(t("process.manualAttached", { title }));
                }}
              />
              <ExplodedViewsPanel
                brand={appliance.brand}
                applianceType={appliance.category}
                modelNumber={appliance.modelNumber}
                strictModelMatch
              />
              {currentStep && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-repair/10 text-repair text-sm font-medium rounded-full">
                        {t("process.phase", { n: currentStep.phase })}
                      </span>
                      <span className="text-sm text-muted-foreground">{phaseLabel(currentStep.phaseName)}</span>
                    </div>
                    {currentStep.isSafetyCritical && (
                      <span className="px-3 py-1 bg-destructive/10 text-destructive text-sm font-medium rounded-full flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {t("process.safety")}
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-foreground mb-2">{currentStep.title}</h3>
                  <p className="text-muted-foreground mb-4">{currentStep.description}</p>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    {currentStep.estimatedTime && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{t("process.estimated", { time: currentStep.estimatedTime })}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Wrench className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Complete Button */}
                  <Button
                    onClick={handleCompleteStep}
                    disabled={isStepCompleted}
                    className={`w-full ${isStepCompleted ? "bg-refurbish hover:bg-refurbish/90" : "bg-repair hover:bg-repair/90"}`}
                  >
                    {isStepCompleted ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t("process.completed")}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t("process.markComplete")}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStepIndex === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("process.previous")}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t("process.completeAll", { done: completedSteps.length, total: steps.length })}
                </span>
                {!(completedSteps.length === steps.length && steps.length > 0) && (
                  <Button
                    onClick={handleNext}
                    disabled={currentStepIndex >= steps.length - 1}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {t("process.next")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* Finish & Passport actions */}
              {id && (
                <WorkflowFinishActions
                  applianceId={id}
                  workOrderId={workOrderId}
                  workflowType="repair"
                  brand={appliance.brand}
                  modelNumber={appliance.modelNumber}
                  category={appliance.category}
                  applianceName={appliance.name}
                  summaryData={{
                    stepsCompleted: completedSteps.length,
                    totalSteps: steps.length,
                  }}
                  allStepsComplete={completedSteps.length === steps.length && steps.length > 0}
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Steps Overview */}
              <div className="bg-card rounded-2xl border border-border p-4">
                <h4 className="font-semibold text-foreground mb-3">{t("process.stepsOverview")}</h4>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {steps.map((step, idx) => {
                    const done = completedSteps.includes(idx);
                    const isCurrent = idx === currentStepIndex;
                    return (
                      <button
                        key={step.id}
                        onClick={() => setCurrentStepIndex(idx)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                          isCurrent ? "bg-primary/10 text-primary font-medium" : done ? "text-muted-foreground" : "text-foreground hover:bg-muted"
                        }`}
                      >
                        {done ? (
                          <CheckCircle className="w-4 h-4 text-refurbish flex-shrink-0" />
                        ) : (
                          <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${isCurrent ? "border-primary" : "border-muted-foreground/40"}`} />
                        )}
                        <span className="truncate">{step.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* AI Assistant */}
              <div className="bg-card rounded-2xl border border-border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-semibold text-foreground">{t("process.aiAssistant")}</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {manualTitle
                    ? t("process.aiActive", { title: manualTitle })
                    : t("process.aiPlaceholder")}
                </p>
                <Button variant="outline" className="w-full" size="sm" onClick={() => setShowAIAssistant(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t("process.askForHelp")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* AI Assistant Dialog */}
        <AIAssistantDialog
          open={showAIAssistant}
          onOpenChange={setShowAIAssistant}
          currentStep={{
            phase: currentStep?.phase ?? 1,
            title: currentStep?.title ?? "Repair",
          }}
          modelNumber={appliance.modelNumber}
          manualId={manualId}
          manualTitle={manualTitle}
          manualIsFallback={manualIsFallback}
          manualSeriesLabel={seriesLabelFor(appliance.modelNumber)}
          applianceBrand={appliance.brand}
          onManualUploaded={(mid, title) => {
            setManualId(mid);
            setManualTitle(title);
            setManualIsFallback(false);
          }}
        />
      </main>
    </div>
  );
};

export default RepairProcess;
